import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import CustomLoading from '../../util_components/custom_loading';
import { get_api_url } from '../../../utils/urls';
import { get_api, post_api } from '../../../redux/api_funcs';
import is_overdue, { is_terminal } from './overdue';
import { closed_by_line } from './closed_by';
import { priority_of, PRIORITY_META } from './priority';
import { Btn, Chip, Segmented, btnStyle } from './ui';
import {
  default_profile_filter,
  filter_profile_tasks,
  profile_meta_line,
  profile_rule_text,
  profile_status_chip,
  profile_task_counts
} from './profile_tasks';

// Per-student sales tasks (one card per task), shown in a popup from the
// "Sales Task List" button on the student profile. Existing tasks link to the
// standalone task detail view; missing ones offer a "Create" action that
// generates the task on the fly. The popup lists every task, including system
// closes the list page hides; Open/Closed/All splits them client-side.
class SalesTasks extends Component {
  state = { items: [], loading: false, error: '', creating: {}, filter: null };

  componentDidMount() {
    this.load();
  }

  load = () => {
    this.setState({ loading: true, error: '' });
    const url = get_api_url(`/v2/sales_task_list/student_tasks/${this.props.student_uuid}`);
    get_api(url, true)
      .then((res) => {
        this.setState({ items: res.data.data.items || [], loading: false });
      })
      .catch((e) => {
        let err = 'Failed to load sales tasks';
        if (e.response?.data?.reason) err = e.response.data.reason;
        this.setState({ error: err, loading: false });
      });
  };

  // Generate a task for this student on the fly, then open its detail view
  // and refresh the popup.
  create_task = (item) => {
    this.setState((prev) => ({ creating: { ...prev.creating, [item.task_type]: true } }));
    const url = get_api_url('/v2/sales_task_list/create_task');
    post_api(url, { student_uuid: this.props.student_uuid, task_type: item.task_type }, true)
      .then((res) => {
        const uuid = res?.data?.data?.uuid;
        if (uuid) {
          window.open(
            `/sales-task-form?uuid=${encodeURIComponent(uuid)}`,
            '_blank',
            'noopener,noreferrer'
          );
        }
        this.load();
      })
      .catch((e) => {
        let err = 'Failed to create sales task';
        if (e.response?.data?.reason) err = e.response.data.reason;
        this.setState({ error: err });
      })
      .finally(() =>
        this.setState((prev) => {
          const creating = { ...prev.creating };
          delete creating[item.task_type];
          return { creating };
        })
      );
  };

  render_row = (item) => {
    const creating = !!this.state.creating[item.task_type];
    const label = item.task_label || item.task_type || '—';
    const rule = profile_rule_text(item);
    const status = profile_status_chip(item);
    const closed = item.exists ? closed_by_line(item) : '';
    const overdue = item.exists && is_overdue(item);
    const prio = PRIORITY_META[priority_of(item)];
    return (
      <li
        key={item.uuid || item.task_type}
        className="grid gap-2.5 border-t border-line-subtle px-4 py-3 first:border-t-0"
        style={{ gridTemplateColumns: 'minmax(0,1fr) 104px' }}
      >
        <div className="flex min-w-0 flex-col gap-1">
          <div
            className="overflow-hidden text-[13px] font-semibold text-content-primary [-webkit-box-orient:vertical] [-webkit-line-clamp:2] [display:-webkit-box]"
            title={label}
          >
            {label}
            {rule ? (
              <span className="text-xs font-normal text-content-muted"> · {rule}</span>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-1">
            {item.exists ? (
              <Chip tone={prio.label === 'P0' ? 'error' : 'neutral'} title={prio.title}>
                {prio.label}
              </Chip>
            ) : null}
            <Chip tone={status.tone}>{status.label}</Chip>
          </div>
          {closed ? <div className="text-xs text-content-muted">{closed}</div> : null}
          <div className="text-[11.5px] text-content-muted">
            {profile_meta_line(item, Date.now())}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 text-right">
          {item.due_datetime ? (
            <>
              <div className="text-[11px] text-content-muted">{overdue ? 'Overdue' : 'Due'}</div>
              <div
                className={`text-[12.5px] leading-tight ${
                  overdue
                    ? 'font-semibold text-state-error-text'
                    : 'font-medium text-content-primary'
                }`}
              >
                {item.due_datetime}
              </div>
            </>
          ) : null}
          <div className="mt-auto w-full">
            {item.exists ? (
              <Link
                to={`/sales-task-form?uuid=${encodeURIComponent(item.uuid)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="no-underline hover:no-underline"
                style={{ ...btnStyle('outline', 'row'), width: '100%', boxSizing: 'border-box' }}
              >
                {is_terminal(item) ? 'View' : 'Open task'}
              </Link>
            ) : (
              <Btn
                variant="outline"
                size="row"
                block
                disabled={creating}
                onClick={() => this.create_task(item)}
                style={creating ? { cursor: 'wait' } : undefined}
              >
                {creating ? 'Creating…' : 'Create'}
              </Btn>
            )}
          </div>
        </div>
      </li>
    );
  };

  render() {
    const { items, loading, error, filter: picked } = this.state;
    if (loading) return <CustomLoading />;

    if (error) {
      return (
        <div className="text-[13px]" style={{ color: 'var(--state-error-text)' }}>
          {error}
        </div>
      );
    }
    if (items.length === 0) {
      return (
        <div className="text-[13px] text-content-muted">No sales tasks for this member yet.</div>
      );
    }

    const filter = picked || default_profile_filter(items);
    const counts = profile_task_counts(items);
    const shown = filter_profile_tasks(items, filter);
    const seg = (text, n) => (
      <>
        {text} <span className="text-[11px] font-normal text-content-muted">{n}</span>
      </>
    );
    return (
      <div className="text-[13px] text-content-primary">
        <Segmented
          label="Show"
          value={filter}
          onChange={(f) => this.setState({ filter: f })}
          options={[
            { value: 'open', label: seg('Open', counts.open) },
            { value: 'closed', label: seg('Closed', counts.closed) },
            { value: 'all', label: 'All' }
          ]}
        />
        {shown.length === 0 ? (
          <div className="px-4 py-6 text-center text-xs text-content-muted">
            {filter === 'open' ? 'No open sales tasks.' : 'No closed sales tasks.'}
          </div>
        ) : (
          <ul className="m-0 mt-2 list-none rounded-[10px] border border-line-subtle p-0">
            {shown.map(this.render_row)}
          </ul>
        )}
      </div>
    );
  }
}

SalesTasks.propTypes = {
  student_uuid: PropTypes.string.isRequired
};

export default SalesTasks;
